import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { Timestamp } from '@applied/proto/google/protobuf/timestamp_pb';
import { useQuery } from '@tanstack/react-query';
import { getCandidate } from 'api/candidate_request';
import { CandidateExperienceQueryKeys } from 'api/query_keys';
import { checkExists } from 'api/typing';

import { parseBenefitsType } from 'components/WebOfferPage/benefits_classes';
import { BenefitsSection } from 'components/WebOfferPage/BenefitsSection/BenefitsSection';
import { EquitySection } from 'components/WebOfferPage/EquitySection/EquitySection';
import { HeroRoleDivider } from 'components/WebOfferPage/HeroRoleDivider/HeroRoleDivider';
import { HeroSection } from 'components/WebOfferPage/HeroSection/HeroSection';
import { Navbar } from 'components/WebOfferPage/Navbar/Navbar';
import { NdaSection } from 'components/WebOfferPage/NdaSection/NdaSection';
import { NumbersReceivedModal } from 'components/WebOfferPage/NumbersReceivedModal/NumbersReceivedModal';
import { RoleSection } from 'components/WebOfferPage/RoleSection/RoleSection';
import { SliderSection } from 'components/WebOfferPage/SliderSection/SliderSection';
import { UserActionSection } from 'components/WebOfferPage/UserActionSection/UserActionSection';
import {
  syncCompensationInfo,
  syncJobInfo,
  syncSelection,
  syncSelectionsFromCompensation,
  useWebOffer,
  WebOfferContextProvider,
} from 'components/WebOfferPage/web_offer_context';
import { getWebOffer } from 'components/WebOfferPage/web_offer_requests';

import './web-offer-page.scss';

import { CultureSection } from './CultureSection/CultureSection';
import { ResourcesSection } from './ResourcesSection/ResourcesSection';

export type WebOfferPageParams = {
  candidateEmail?: string;
};

export function WebOfferPage(): JSX.Element {
  const { candidateEmail } = useParams<WebOfferPageParams>();
  const history = useHistory();
  const {
    data: webOffer,
    isLoading: webOfferIsLoading,
    error: webOfferError,
  } = useQuery({
    queryKey: [CandidateExperienceQueryKeys.WEB_OFFER, candidateEmail],
    queryFn: () => getWebOffer(history, candidateEmail),
  });
  const {
    data: candidate,
    isLoading: candidateIsLoading,
    error: candidateError,
  } = useQuery({
    queryKey: [CandidateExperienceQueryKeys.CANDIDATE, candidateEmail],
    queryFn: () => getCandidate(history, candidateEmail),
  });
  const isLoading = webOfferIsLoading || candidateIsLoading;
  const error = webOfferError || candidateError;

  const {
    setCandidate,
    setManager,
    setTeamMembers,
    jobInfo: { setJobTitle, setAtsJobPostUrl, setTeamName, setTeamPageUrl },
    compensationInfo: {
      setCurrency,
      setBenefitsType: setCompensationBenefitsType,
      setBaseSalarySubdivisions,
      setSalaryThresholdPercentage,
      setMaxSignOnBonusSubdivisions,
      setBaseEquityUsdCents,
      setSalaryEquityRatio,
      setSignOnBonusEquityRatio,
      setRelocationBonusSubdivisions,
      setAnnualBonusType,
      setAnnualBonusValue,
      setIsSalaryStatic,
      setIsSignOnBonusStatic,
    },
    setMarketConditions,
    setSalarySubdivisions,
    setSignOnBonusSubdivisions,
    setDeadline,
    deadline,
    selection: {
      isFinal,
      setIsFinal,
      setSalarySubdivisions: setSelectionSalarySubdivisions,
      setSignOnBonusSubdivisions: setSelectionSignOnBonusSubdivisions,
      setEquityUsdCents: setSelectionEquityUsdCents,
    },
    setBenefitsType,
    setShow50HourWorkWeekVideo,
  } = useWebOffer();
  const [isNumbersReceivedModalOpen, setIsNumbersReceivedModalOpen] = useState(false);

  useEffect(
    function updateWebOfferContext() {
      if (
        !webOffer ||
        !webOffer.configuration ||
        !webOffer.configuration.manager ||
        !webOffer.configuration.teamMembers ||
        !webOffer.configuration.jobInfo ||
        !webOffer.configuration.compensationInfo ||
        !webOffer.configuration.marketConditions ||
        !webOffer.configuration.deadline ||
        !checkExists(webOffer.configuration.show50HourWorkWeekVideo)
      ) {
        return;
      }
      setManager(webOffer.configuration.manager);
      setTeamMembers(webOffer.configuration.teamMembers);
      syncJobInfo({
        protoJobInfo: webOffer.configuration.jobInfo,
        setJobTitle,
        setAtsJobPostUrl,
        setTeamName,
        setTeamPageUrl,
      });
      syncCompensationInfo({
        protoCompensationInfo: webOffer.configuration.compensationInfo,
        setCurrency,
        setCompensationBenefitsType,
        setBaseSalarySubdivisions,
        setSalaryThresholdPercentage,
        setMaxSignOnBonusSubdivisions,
        setBaseEquityUsdCents,
        setSalaryEquityRatio,
        setSignOnBonusEquityRatio,
        setRelocationBonusSubdivisions,
        setAnnualBonusType,
        setAnnualBonusValue,
        setIsSalaryStatic,
        setIsSignOnBonusStatic,
      });
      setMarketConditions(webOffer.configuration.marketConditions);
      setBenefitsType(parseBenefitsType(webOffer.configuration.compensationInfo.benefitsType));
      // Use selection values if available, otherwise fall back to defaults from compensationInfo
      if (webOffer.selection) {
        syncSelection({
          protoSelection: webOffer.selection,
          setSalarySubdivisions,
          setSignOnBonusSubdivisions,
          setIsFinal,
          setSelectionSalarySubdivisions,
          setSelectionSignOnBonusSubdivisions,
          setSelectionEquityUsdCents,
        });
      } else {
        syncSelectionsFromCompensation({
          protoCompensationInfo: webOffer.configuration.compensationInfo,
          setSalarySubdivisions,
          setSignOnBonusSubdivisions,
        });
      }
      setDeadline(webOffer.configuration.deadline);
      setShow50HourWorkWeekVideo(webOffer.configuration.show50HourWorkWeekVideo);
    },
    [
      setManager,
      setTeamMembers,
      setJobTitle,
      setAtsJobPostUrl,
      setTeamName,
      setTeamPageUrl,
      setCurrency,
      setCompensationBenefitsType,
      setBaseSalarySubdivisions,
      setSalaryThresholdPercentage,
      setMaxSignOnBonusSubdivisions,
      setBaseEquityUsdCents,
      setSalaryEquityRatio,
      setSignOnBonusEquityRatio,
      setRelocationBonusSubdivisions,
      setAnnualBonusType,
      setAnnualBonusValue,
      setIsSalaryStatic,
      setIsSignOnBonusStatic,
      setMarketConditions,
      setSalarySubdivisions,
      setSignOnBonusSubdivisions,
      setIsFinal,
      setSelectionSalarySubdivisions,
      setSelectionSignOnBonusSubdivisions,
      setSelectionEquityUsdCents,
      setDeadline,
      setBenefitsType,
      setShow50HourWorkWeekVideo,
      webOffer,
    ],
  );

  useEffect(
    function updateCandidateContext() {
      if (!candidate) {
        return;
      }
      setCandidate(candidate);
    },
    [setCandidate, candidate],
  );

  useEffect(
    function updateTabTitle() {
      // Don't update the title until the page is loaded and there is no error.
      if (isLoading || !!error) {
        return;
      }
      document.title = 'Applied Intuition - Web Offer';
    },
    [isLoading, error],
  );

  useEffect(
    function checkDeadlineAndAcceptance() {
      if (!deadline || !isFinal) {
        return;
      }
      const deadlineDate = Timestamp.toDate(deadline);
      const now = new Date();
      if (deadlineDate < now) {
        setIsNumbersReceivedModalOpen(true);
      }
    },
    [deadline, isFinal],
  );

  if (isLoading) {
    return <Typography variant="header">Loading...</Typography>;
  }
  if (error) {
    history.push('/error');
    return <Typography variant="header">Error</Typography>;
  }
  return (
    <>
      {isNumbersReceivedModalOpen && (
        <NumbersReceivedModal onClose={() => setIsNumbersReceivedModalOpen(false)} />
      )}
      <Flex className="web-offer-page" direction="column" alignment="center">
        <Navbar />
        <Flex className="web-offer-page-body" direction="column" alignment="center">
          <NdaSection />
          <HeroSection />
          <HeroRoleDivider />
          <RoleSection />
          <SliderSection />
          <EquitySection />
          <BenefitsSection />
          <CultureSection />
          <ResourcesSection />
          <UserActionSection viewOnly={Boolean(candidateEmail)} />
        </Flex>
      </Flex>
    </>
  );
}

export function WebOfferPageWrapper(): JSX.Element {
  return (
    <WebOfferContextProvider>
      <WebOfferPage />
    </WebOfferContextProvider>
  );
}
